import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { TrackingDevice } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    DisplayDeviceStatus,
    InputPosition,
    PositionDisplay,
    InputNullableFloat,
    DisplayBattery,
    InputNullableDateTime,
    DateComp,
    DisplayEvent,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class TrackingDeviceGuiDef extends GuiDef<TrackingDevice> {
    constructor() {
        super('TrackingDevice');

        const DeviceNumber = new FieldDef<TrackingDevice>();
        DeviceNumber.Name = 'DeviceNumber';
        DeviceNumber.FieldName = 'DeviceNumber';
        DeviceNumber.DefinedFor = 'Both';
        DeviceNumber.TranslationKey = 'trackingdevice.devicenumber';
        DeviceNumber.Editable = false;
        DeviceNumber.Filterable = true;
        DeviceNumber.Sortable = true;
        DeviceNumber.CssClasses = '';
        DeviceNumber.value = (trackingdevice) => trackingdevice.DeviceNumber;
        DeviceNumber.setValue = () => {};
        DeviceNumber.DetailEditor = markRaw(InputText);
        DeviceNumber.DetailEditorProps = {};
        DeviceNumber.CellEditor = markRaw(InputText);
        DeviceNumber.CellEditorProps = {};
        DeviceNumber.CellRenderer = markRaw(ValidatedText);
        DeviceNumber.CellRendererProps = {};
        DeviceNumber.Validator = null;
        DeviceNumber.SortDirection = undefined;
        DeviceNumber.cellClassRules = undefined;
        DeviceNumber.MinWidth = undefined;
        DeviceNumber.MaxWidth = undefined;
        DeviceNumber.Tooltip = null;
        this.Fields.push(DeviceNumber);

        const IMEI = new FieldDef<TrackingDevice>();
        IMEI.Name = 'IMEI';
        IMEI.FieldName = 'IMEI';
        IMEI.DefinedFor = 'Both';
        IMEI.TranslationKey = 'trackingdevice.imei';
        IMEI.Editable = false;
        IMEI.Filterable = true;
        IMEI.Sortable = true;
        IMEI.CssClasses = '';
        IMEI.value = (trackingdevice) => trackingdevice.IMEI;
        IMEI.setValue = () => {};
        IMEI.DetailEditor = markRaw(InputText);
        IMEI.DetailEditorProps = {};
        IMEI.CellEditor = markRaw(InputText);
        IMEI.CellEditorProps = {};
        IMEI.CellRenderer = markRaw(ValidatedText);
        IMEI.CellRendererProps = {};
        IMEI.Validator = null;
        IMEI.SortDirection = undefined;
        IMEI.cellClassRules = undefined;
        IMEI.MinWidth = undefined;
        IMEI.MaxWidth = undefined;
        IMEI.Tooltip = null;
        this.Fields.push(IMEI);

        const Type = new FieldDef<TrackingDevice>();
        Type.Name = 'Type';
        Type.FieldName = 'Type';
        Type.DefinedFor = 'Both';
        Type.TranslationKey = 'trackingdevice.type';
        Type.Editable = false;
        Type.Filterable = true;
        Type.Sortable = true;
        Type.CssClasses = '';
        Type.value = (trackingdevice) => trackingdevice.Type;
        Type.setValue = () => {};
        Type.DetailEditor = markRaw(InputText);
        Type.DetailEditorProps = {};
        Type.CellEditor = markRaw(InputText);
        Type.CellEditorProps = {};
        Type.CellRenderer = markRaw(ValidatedText);
        Type.CellRendererProps = {};
        Type.Validator = null;
        Type.SortDirection = undefined;
        Type.cellClassRules = undefined;
        Type.MinWidth = undefined;
        Type.MaxWidth = undefined;
        Type.Tooltip = null;
        this.Fields.push(Type);

        const CurrentStatus = new FieldDef<TrackingDevice>();
        CurrentStatus.Name = 'CurrentStatus';
        CurrentStatus.FieldName = 'CurrentStatus';
        CurrentStatus.DefinedFor = 'Both';
        CurrentStatus.TranslationKey = 'trackingdevice.currentstatus';
        CurrentStatus.Editable = false;
        CurrentStatus.Filterable = false;
        CurrentStatus.Sortable = true;
        CurrentStatus.CssClasses = '';
        CurrentStatus.value = (trackingdevice) => trackingdevice.CurrentStatus;
        CurrentStatus.setValue = () => {};
        CurrentStatus.DetailEditor = markRaw(InputText);
        CurrentStatus.DetailEditorProps = {};
        CurrentStatus.CellEditor = markRaw(InputText);
        CurrentStatus.CellEditorProps = {};
        CurrentStatus.CellRenderer = markRaw(DisplayDeviceStatus);
        CurrentStatus.CellRendererProps = {};
        CurrentStatus.Validator = null;
        CurrentStatus.SortDirection = undefined;
        CurrentStatus.cellClassRules = undefined;
        CurrentStatus.MinWidth = undefined;
        CurrentStatus.MaxWidth = undefined;
        CurrentStatus.Tooltip = null;
        this.Fields.push(CurrentStatus);

        const LastKnownPosition = new FieldDef<TrackingDevice>();
        LastKnownPosition.Name = 'LastKnownPosition';
        LastKnownPosition.FieldName = 'LastKnownPosition';
        LastKnownPosition.DefinedFor = 'List';
        LastKnownPosition.TranslationKey = 'trackingdevice.lastknownposition';
        LastKnownPosition.Editable = false;
        LastKnownPosition.Filterable = false;
        LastKnownPosition.Sortable = true;
        LastKnownPosition.CssClasses = '';
        LastKnownPosition.value = (trackingdevice) => trackingdevice.LastKnownPosition;
        LastKnownPosition.setValue = () => {};
        LastKnownPosition.DetailEditor = markRaw(InputPosition);
        LastKnownPosition.DetailEditorProps = {};
        LastKnownPosition.CellEditor = markRaw(InputPosition);
        LastKnownPosition.CellEditorProps = {};
        LastKnownPosition.CellRenderer = markRaw(PositionDisplay);
        LastKnownPosition.CellRendererProps = {};
        LastKnownPosition.Validator = null;
        LastKnownPosition.SortDirection = undefined;
        LastKnownPosition.cellClassRules = undefined;
        LastKnownPosition.MinWidth = undefined;
        LastKnownPosition.MaxWidth = undefined;
        LastKnownPosition.Tooltip = null;
        this.Fields.push(LastKnownPosition);

        const BatteryLevel = new FieldDef<TrackingDevice>();
        BatteryLevel.Name = 'BatteryLevel';
        BatteryLevel.FieldName = 'BatteryLevel';
        BatteryLevel.DefinedFor = 'Both';
        BatteryLevel.TranslationKey = 'trackingdevice.batterylevel';
        BatteryLevel.Editable = false;
        BatteryLevel.Filterable = true;
        BatteryLevel.Sortable = true;
        BatteryLevel.CssClasses = '';
        BatteryLevel.value = (trackingdevice) => trackingdevice.BatteryLevel;
        BatteryLevel.setValue = () => {};
        BatteryLevel.DetailEditor = markRaw(InputNullableFloat);
        BatteryLevel.DetailEditorProps = {};
        BatteryLevel.CellEditor = markRaw(InputNullableFloat);
        BatteryLevel.CellEditorProps = {};
        BatteryLevel.CellRenderer = markRaw(DisplayBattery);
        BatteryLevel.CellRendererProps = {};
        BatteryLevel.Validator = null;
        BatteryLevel.SortDirection = undefined;
        BatteryLevel.cellClassRules = undefined;
        BatteryLevel.MinWidth = undefined;
        BatteryLevel.MaxWidth = undefined;
        BatteryLevel.Tooltip = null;
        this.Fields.push(BatteryLevel);

        const LastBatteryUpdate = new FieldDef<TrackingDevice>();
        LastBatteryUpdate.Name = 'LastBatteryUpdate';
        LastBatteryUpdate.FieldName = 'LastBatteryUpdate';
        LastBatteryUpdate.DefinedFor = 'Both';
        LastBatteryUpdate.TranslationKey = 'trackingdevice.lastbatteryupdate';
        LastBatteryUpdate.Editable = false;
        LastBatteryUpdate.Filterable = true;
        LastBatteryUpdate.Sortable = true;
        LastBatteryUpdate.CssClasses = '';
        LastBatteryUpdate.value = (trackingdevice) => trackingdevice.LastBatteryUpdate;
        LastBatteryUpdate.setValue = () => {};
        LastBatteryUpdate.DetailEditor = markRaw(InputNullableDateTime);
        LastBatteryUpdate.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        LastBatteryUpdate.CellEditor = markRaw(InputNullableDateTime);
        LastBatteryUpdate.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        LastBatteryUpdate.CellRenderer = markRaw(DateComp);
        LastBatteryUpdate.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        LastBatteryUpdate.Validator = null;
        LastBatteryUpdate.SortDirection = 'Descending';
        LastBatteryUpdate.cellClassRules = [
            {
                func: (cellData: any, row: TrackingDevice) =>
                    row.LastBatteryUpdate == null ? false : new Date(row.LastBatteryUpdate) > new Date(new Date().getTime() - 300000),
                className: 'grid-cell-success',
            },
        ];
        LastBatteryUpdate.MinWidth = 180;
        LastBatteryUpdate.MaxWidth = undefined;
        LastBatteryUpdate.Tooltip = null;
        this.Fields.push(LastBatteryUpdate);

        const DisplayName = new FieldDef<TrackingDevice>();
        DisplayName.Name = 'DisplayName';
        DisplayName.FieldName = 'DisplayName';
        DisplayName.DefinedFor = 'Both';
        DisplayName.TranslationKey = 'trackingdevice.displayname';
        DisplayName.Editable = false;
        DisplayName.Filterable = true;
        DisplayName.Sortable = true;
        DisplayName.CssClasses = '';
        DisplayName.value = (trackingdevice) => trackingdevice.DisplayName;
        DisplayName.setValue = () => {};
        DisplayName.DetailEditor = markRaw(InputText);
        DisplayName.DetailEditorProps = {};
        DisplayName.CellEditor = markRaw(InputText);
        DisplayName.CellEditorProps = {};
        DisplayName.CellRenderer = markRaw(ValidatedText);
        DisplayName.CellRendererProps = {};
        DisplayName.Validator = null;
        DisplayName.SortDirection = undefined;
        DisplayName.cellClassRules = undefined;
        DisplayName.MinWidth = undefined;
        DisplayName.MaxWidth = undefined;
        DisplayName.Tooltip = null;
        this.Fields.push(DisplayName);

        const LastEvent = new FieldDef<TrackingDevice>();
        LastEvent.Name = 'LastEvent';
        LastEvent.FieldName = 'LastEvent';
        LastEvent.DefinedFor = 'Both';
        LastEvent.TranslationKey = 'trackingdevice.lastevent';
        LastEvent.Editable = false;
        LastEvent.Filterable = true;
        LastEvent.Sortable = true;
        LastEvent.CssClasses = '';
        LastEvent.value = (trackingdevice) => trackingdevice.LastEvent;
        LastEvent.setValue = () => {};
        LastEvent.DetailEditor = markRaw(InputText);
        LastEvent.DetailEditorProps = {};
        LastEvent.CellEditor = markRaw(InputText);
        LastEvent.CellEditorProps = {};
        LastEvent.CellRenderer = markRaw(DisplayEvent);
        LastEvent.CellRendererProps = {};
        LastEvent.Validator = null;
        LastEvent.SortDirection = undefined;
        LastEvent.cellClassRules = undefined;
        LastEvent.MinWidth = undefined;
        LastEvent.MaxWidth = undefined;
        LastEvent.Tooltip = null;
        this.Fields.push(LastEvent);
    }

    public async LoadDependencies() {}
}
